import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DailyHabitCatalogItem, FundamentalCatalogItem } from '@/api/types';
import { DailyHabitsModal } from './DailyHabitsModal';
import { FundamentalCard, fundamentalColor, fundamentalDisplayName, type HabitSelection } from './FundamentalCard';
import { activeHabitsForAttitude, allActiveHabits, orphanHabits } from './habitSelection';
import './FundamentalsCatalog.css';

function resolveHabits(selection: HabitSelection): DailyHabitCatalogItem[] {
  const { fundamental, habitScope } = selection;
  if (habitScope.kind === 'attitude') {
    return activeHabitsForAttitude(fundamental, habitScope.attitudeId);
  }
  if (habitScope.kind === 'orphans') {
    return orphanHabits(fundamental);
  }
  return allActiveHabits(fundamental);
}

export interface FundamentalsCatalogGridProps {
  /** Catálogo completo, en cualquier orden. */
  catalogData: FundamentalCatalogItem[];
  /** Idioma corto de la UI (es, en, pt…). */
  lang: string;
}

/**
 * Rejilla del catálogo de fundamentales. Es dueña del estado del modal de hábitos,
 * de modo que la pantalla de umbral y el menú Fundamentales compartan el comportamiento
 * sin duplicar lógica.
 */
export function FundamentalsCatalogGrid({ catalogData, lang }: FundamentalsCatalogGridProps) {
  const { t } = useTranslation();
  const [selection, setSelection] = useState<HabitSelection | null>(null);
  const orderedCatalog = [...catalogData].sort((first, second) => first.order - second.order);

  return (
    <>
      <div className="fund-cat-grid">
        {orderedCatalog.map(fundamental => (
          <FundamentalCard
            key={fundamental.id}
            fundamental={fundamental}
            lang={lang}
            orphanLabel={t('fundamentals.otherHabits')}
            onSelect={setSelection}
          />
        ))}
      </div>

      {selection && (
        <DailyHabitsModal
          isOpen
          onClose={() => setSelection(null)}
          fundamentalName={fundamentalDisplayName(selection.fundamental, lang)}
          fundamentalIcon={selection.fundamental.icon}
          color={fundamentalColor(selection.fundamental)}
          attitudeName={selection.attitudeName}
          habits={resolveHabits(selection)}
          lang={lang}
        />
      )}
    </>
  );
}
